'use client';

import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import {
  ApiError,
  TOKEN_KEY,
  apiGet,
  apiPost,
  clearSession,
  getToken,
  isTokenExpired,
  setToken,
} from '@/lib/api';
import { invalidateDashboards } from '@/lib/services/dashboard';

/**
 * The session, as the API issues it: a JWT kept in localStorage and sent on
 * every call by @/lib/api. Who the person is comes from /api/auth/me and
 * /api/me/profile, never from the token alone; the role is only used here
 * to route and to hide menus - the API enforces it.
 *
 * Google / Microsoft sign-in is a full-page trip through the API
 * (/api/auth/external/{provider}/start) that lands on /auth/callback with a
 * one-time code, exchanged for the token by completeExternalSignIn.
 */

export type UserRole =
  | 'CUSTOMER'
  | 'SOLUTION_ARCHITECT'
  | 'PROJECT_MANAGER'
  | 'DEVELOPER'
  | 'DESIGNER'
  | 'QA_ENGINEER'
  | 'DEVOPS_ENGINEER'
  | 'FINANCE_OFFICER'
  | 'SUPPORT_AGENT'
  | 'OPERATIONS_OFFICER'
  | 'ADMIN'
  | 'SUPER_ADMIN'
  // A limited, partner-facing finance role: it may only reach the receipts /
  // export screen (/finance). Deliberately NOT an internal staff role.
  | 'FINANCE_PARTNER';

export type ExternalProvider = 'google' | 'microsoft';

export interface UserProfile {
  id: string;
  authUserId: string;
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber?: string;
  country?: string;
  preferredCurrency: string;
  companyName?: string;
  accountType: 'INDIVIDUAL' | 'BUSINESS';
  role: UserRole;
  status: 'ACTIVE' | 'SUSPENDED' | 'CLOSED';
  verificationStatus: 'UNVERIFIED' | 'PENDING' | 'VERIFIED';
  createdAt: string;
  updatedAt: string;
  emailConfirmedAt: string | null;
  hasPassword: boolean;
  mustResetPassword: boolean;
}

export interface SessionUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roleCode: UserRole;
  emailConfirmedAt: string | null;
  /** The older snake_case spelling some pages still read. */
  email_confirmed_at: string | null;
  hasPassword: boolean;
}

interface AuthContextValue {
  user: SessionUser | null;
  session: { token: string } | null;
  profile: UserProfile | null;
  loading: boolean;
  role: UserRole | null;
  isCustomer: boolean;
  isInternalStaff: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  signUp: (
    email: string,
    password: string,
    metadata: { firstName: string; lastName: string; accountType?: 'INDIVIDUAL' | 'BUSINESS' }
  ) => Promise<{ redirectTo: string }>;
  signIn: (email: string, password: string) => Promise<{ redirectTo: string }>;
  signOut: () => Promise<void>;
  startOAuth: (provider: ExternalProvider, returnUrl?: string) => void;
  completeExternalSignIn: (code: string) => Promise<{ redirectTo: string }>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, newPassword: string) => Promise<void>;
  verifyEmail: (token: string) => Promise<void>;
  resendVerification: () => Promise<void>;
  getCurrentUser: () => Promise<SessionUser | null>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<UserProfile | null>;
  refreshProfile: () => Promise<void>;
}

// --- API shapes --------------------------------------------------------------

interface SessionUserDto {
  userId: number;
  email: string;
  firstName: string;
  lastName: string;
  roleCode: string;
  emailConfirmedAt: string | null;
  hasPassword: boolean;
}

interface LoginResponse {
  token: string;
  expiresAt: string;
  user: SessionUserDto;
}

interface UserProfileDto {
  userId: number;
  email: string;
  firstName: string;
  lastName: string;
  mobileNumber: string | null;
  country: string | null;
  companyName: string | null;
  preferredCurrency: string;
  accountType: string;
  roleCode: string;
  status: string;
  verificationStatus: string;
  emailConfirmedAt: string | null;
  mustResetPassword: boolean;
  hasPassword: boolean;
  createdAt: string;
  updatedAt: string;
}

const AuthContext = createContext<AuthContextValue>({} as AuthContextValue);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const INTERNAL_ROLES: UserRole[] = [
  'SOLUTION_ARCHITECT',
  'PROJECT_MANAGER',
  'DEVELOPER',
  'DESIGNER',
  'QA_ENGINEER',
  'DEVOPS_ENGINEER',
  'FINANCE_OFFICER',
  'SUPPORT_AGENT',
  'OPERATIONS_OFFICER',
  'ADMIN',
  'SUPER_ADMIN',
];

/**
 * Who may open the finance receipts / export screen (/finance): the limited
 * finance partner, the internal finance officer and admins. Mirrors the API's
 * "Finance" authorization policy. FINANCE_PARTNER is intentionally excluded
 * from INTERNAL_ROLES, so it lands here and nowhere else.
 */
export const FINANCE_ROLES: UserRole[] = [
  'FINANCE_PARTNER',
  'FINANCE_OFFICER',
  'ADMIN',
  'SUPER_ADMIN',
];

function mapSessionUser(dto: SessionUserDto): SessionUser {
  return {
    id: String(dto.userId),
    email: dto.email,
    firstName: dto.firstName || '',
    lastName: dto.lastName || '',
    roleCode: (dto.roleCode || 'CUSTOMER') as UserRole,
    emailConfirmedAt: dto.emailConfirmedAt ?? null,
    email_confirmed_at: dto.emailConfirmedAt ?? null,
    hasPassword: Boolean(dto.hasPassword),
  };
}

function mapProfile(dto: UserProfileDto): UserProfile {
  return {
    id: String(dto.userId),
    authUserId: String(dto.userId),
    firstName: dto.firstName || '',
    lastName: dto.lastName || '',
    email: dto.email,
    mobileNumber: dto.mobileNumber ?? undefined,
    country: dto.country ?? undefined,
    preferredCurrency: dto.preferredCurrency || 'USD',
    companyName: dto.companyName ?? undefined,
    accountType: (dto.accountType || 'INDIVIDUAL') as UserProfile['accountType'],
    role: (dto.roleCode || 'CUSTOMER') as UserRole,
    status: (dto.status || 'ACTIVE') as UserProfile['status'],
    verificationStatus: (dto.verificationStatus ||
      'UNVERIFIED') as UserProfile['verificationStatus'],
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    emailConfirmedAt: dto.emailConfirmedAt ?? null,
    hasPassword: Boolean(dto.hasPassword),
    mustResetPassword: Boolean(dto.mustResetPassword),
  };
}

export function getRoleRedirect(role: UserRole | string | null | undefined): string {
  if (!role || role === 'CUSTOMER') return '/customer-dashboard';
  // The finance partner has one screen and no dashboard.
  if (role === 'FINANCE_PARTNER') return '/finance';
  return '/admin-dashboard';
}

/** A page of ours: a relative path, never a scheme or a protocol-relative host. */
export function safeReturnPath(value: string | null | undefined): string | null {
  const v = (value ?? '').trim();
  if (!v.startsWith('/') || v.startsWith('//') || v.startsWith('/\\') || v.includes('://'))
    return null;
  if (v.startsWith('/sign-up-login-screen') || v.startsWith('/auth/')) return null;
  return v;
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const router = useRouter();

  const loadProfile = useCallback(async (): Promise<UserProfile | null> => {
    try {
      return mapProfile(await apiGet<UserProfileDto>('/api/me/profile'));
    } catch {
      return null;
    }
  }, []);

  /** Re-reads who is signed in from the API; clears a dead session. */
  const loadSession = useCallback(async (): Promise<SessionUser | null> => {
    const token = getToken();
    if (!token || isTokenExpired(token)) {
      clearSession();
      setUser(null);
      setProfile(null);
      return null;
    }
    try {
      const me = mapSessionUser(await apiGet<SessionUserDto>('/api/auth/me'));
      setUser(me);
      setProfile(await loadProfile());
      return me;
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) clearSession();
      setUser(null);
      setProfile(null);
      return null;
    }
  }, [loadProfile]);

  useEffect(() => {
    let cancelled = false;
    loadSession().then((me) => {
      if (cancelled) return;
      setLoading(false);
      // Already signed in and on the sign-in page: carry on to the dashboard.
      if (
        me &&
        typeof window !== 'undefined' &&
        window.location.pathname.startsWith('/sign-up-login-screen')
      ) {
        router.replace(getRoleRedirect(me.roleCode));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [loadSession, router]);

  // Signed out in another tab: this tab follows.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === TOKEN_KEY && !event.newValue) {
        setUser(null);
        setProfile(null);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const establish = useCallback(
    async (login: LoginResponse): Promise<{ redirectTo: string }> => {
      setToken(login.token);
      invalidateDashboards();
      const me = mapSessionUser(login.user);
      setUser(me);
      setProfile(await loadProfile());
      setLoading(false);
      return { redirectTo: getRoleRedirect(me.roleCode) };
    },
    [loadProfile]
  );

  const signIn = useCallback(
    async (email: string, password: string) => {
      const login = await apiPost<LoginResponse>('/api/auth/login', {
        email: email.trim(),
        password,
      });
      return establish(login);
    },
    [establish]
  );

  const signUp = useCallback(
    async (
      email: string,
      password: string,
      metadata: { firstName: string; lastName: string; accountType?: 'INDIVIDUAL' | 'BUSINESS' }
    ) => {
      await apiPost('/api/auth/register', {
        email: email.trim(),
        password,
        firstName: metadata.firstName.trim(),
        lastName: metadata.lastName.trim(),
        accountType: metadata.accountType || 'INDIVIDUAL',
      });
      // Verification is not a gate: the account signs straight in and the
      // dashboard shows a banner until the e-mailed link is used.
      return signIn(email, password);
    },
    [signIn]
  );

  const signOut = useCallback(async () => {
    try {
      if (getToken()) await apiPost('/api/auth/logout');
    } catch {
      /* the token is dropped regardless */
    }
    clearSession();
    invalidateDashboards();
    setUser(null);
    setProfile(null);
    router.replace('/sign-up-login-screen');
  }, [router]);

  const startOAuth = useCallback((provider: ExternalProvider, returnUrl?: string) => {
    const safe = safeReturnPath(returnUrl) ?? '/';
    window.location.assign(
      `/api/auth/external/${provider}/start?returnUrl=${encodeURIComponent(safe)}`
    );
  }, []);

  const completeExternalSignIn = useCallback(
    async (code: string) => {
      const login = await apiPost<LoginResponse>('/api/auth/external/handoff', { code });
      return establish(login);
    },
    [establish]
  );

  const forgotPassword = useCallback(async (email: string) => {
    await apiPost('/api/auth/forgot-password', { email: email.trim() });
  }, []);

  const resetPassword = useCallback(async (token: string, newPassword: string) => {
    await apiPost('/api/auth/reset-password', { token, newPassword });
  }, []);

  const verifyEmail = useCallback(
    async (token: string) => {
      await apiPost('/api/auth/verify-email', { token });
      if (getToken()) await loadSession();
    },
    [loadSession]
  );

  const resendVerification = useCallback(async () => {
    await apiPost('/api/auth/resend-verification');
  }, []);

  const getCurrentUser = useCallback(() => loadSession(), [loadSession]);

  const isEmailVerified = () => user?.emailConfirmedAt != null;

  const getUserProfile = useCallback(async (): Promise<UserProfile | null> => {
    if (!getToken()) return null;
    return loadProfile();
  }, [loadProfile]);

  const refreshProfile = useCallback(async () => {
    if (!getToken()) return;
    setProfile(await loadProfile());
  }, [loadProfile]);

  const role = profile?.role ?? user?.roleCode ?? null;
  const isCustomer = role === 'CUSTOMER';
  const isInternalStaff = role ? INTERNAL_ROLES.includes(role) : false;
  const isAdmin = role === 'ADMIN' || role === 'SUPER_ADMIN';
  const isSuperAdmin = role === 'SUPER_ADMIN';
  const token = typeof window === 'undefined' ? null : getToken();

  const value: AuthContextValue = {
    user,
    session: user && token ? { token } : null,
    profile,
    loading,
    role,
    isCustomer,
    isInternalStaff,
    isAdmin,
    isSuperAdmin,
    signUp,
    signIn,
    signOut,
    startOAuth,
    completeExternalSignIn,
    forgotPassword,
    resetPassword,
    verifyEmail,
    resendVerification,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
